import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { faTimes, faCog, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { UsersService } from '../../services/users.service';
import { AlertService } from '../alert-service/alert-service';

interface ConfigurationItem {
  id: string;
  key: string;
  display_label?: string;
  type: string;
  has_access: boolean;
}

@Component({
  selector: 'app-modal-user-configurations',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    FontAwesomeModule,
    TranslateModule
  ],
  templateUrl: './modal-user-configurations.component.html',
  styleUrls: ['./modal-user-configurations.component.scss']
})
export class ModalUserConfigurationsComponent implements OnInit, OnChanges {
  @Input() show = false;
  @Input() userId: string = '';
  @Input() username: string = '';
  @Output() close = new EventEmitter<void>();
  @Output() configurationsChanged = new EventEmitter<void>();

  faTimes = faTimes;
  faCog = faCog;
  faSpinner = faSpinner;

  loading = false;
  saving = false;
  configurations: ConfigurationItem[] = [];
  
  configsByType: { [key: string]: ConfigurationItem[] } = {};

  constructor(
    private usersService: UsersService,
    private alertService: AlertService,
    private translate: TranslateService
  ) {}

  ngOnInit() {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['show'] && this.show && this.userId) {
      this.loadConfigurations();
    }
  }

  loadConfigurations() {
    this.loading = true;
    this.usersService.listUserConfigurations(this.userId).subscribe({
      next: (response) => {
        this.configurations = response.configurations || [];
        this.groupConfigurationsByType();
        this.loading = false;
      },
      error: (err) => {
        this.alertService.showError(err);
        this.loading = false;
        this.configurations = [];
        this.configsByType = {};
      }
    });
  }

  groupConfigurationsByType() {
    this.configsByType = {};
    this.configurations.forEach(config => {
      if (!this.configsByType[config.type]) {
        this.configsByType[config.type] = [];
      }
      this.configsByType[config.type].push(config);
    });
  }

  getTypeLabel(type: string): string {
    const labels: { [key: string]: string } = {
      'delfos_server': 'SETTINGS.PREFERENCES.DELFOS_SERVER',
      'delfos_llm': 'SETTINGS.PREFERENCES.DELFOS_LLM',
      'di2winocr_server': 'SETTINGS.PREFERENCES.OCR_SERVER',
      'di2winocr_features': 'SETTINGS.PREFERENCES.OCR_FEATURES'
    };
    return labels[type] || type;
  }

  getConfigTypes(): string[] {
    return Object.keys(this.configsByType).sort();
  }

  onCheckboxChange(config: ConfigurationItem, event: Event) {
    const checkbox = event.target as HTMLInputElement;
    const newValue = checkbox.checked;
    
    const originalValue = config.has_access;
    config.has_access = newValue;
    
    this.saving = true;
    
    const operation = newValue
      ? this.usersService.addConfigurationToUser(this.userId, config.id)
      : this.usersService.removeConfigurationFromUser(this.userId, config.id);
    
    operation.subscribe({
      next: () => {
        this.saving = false;
        this.configurationsChanged.emit();
        const key = newValue 
          ? 'USERS.CONFIGURATIONS.ADD_SUCCESS' 
          : 'USERS.CONFIGURATIONS.REMOVE_SUCCESS';
        this.alertService.showSuccess(this.translate.instant(key));
      },
      error: (err) => {
        config.has_access = originalValue;
        checkbox.checked = originalValue;
        this.saving = false;
        this.alertService.showError(err);
      }
    });
  }

  onClose() {
    this.close.emit();
  }
}
